package com.bitterinfant.webui

import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.compositionLocalOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/** Fixed height of the tab strip in px — callers that overlap it over a banner (e.g. matching
 *  a negative marginTop against an ImageBanner's added height) can rely on this exact number
 *  instead of estimating rendered button height. */
const val TABS_HEIGHT_PX = 56

/** Slug of the [Tabs] panel being composed (null outside one), for deep links to its clips. */
val LocalTabSlug = compositionLocalOf<String?> { null }

/**
 * A simple client-side tab strip. Tracks its own selection (starting at tab [initial]); [content]
 * receives the active index so the caller can render the matching panel, e.g. with a `when`.
 * [onSelect] lets the caller react to tab changes outside the panel (e.g. swapping a banner image).
 * Pass `DeepLink.tabIndex(labels)` as [initial] to honor a `?tab=` deep link; switching tabs
 * updates `?tab=` in the address bar to match (see [DeepLink.showTab]).
 */
@Composable
fun Tabs(labels: List<String>, initial: Int = 0, onSelect: (Int) -> Unit = {}, content: @Composable (active: Int) -> Unit) {
    var active by remember { mutableStateOf(initial) }
    Div(attrs = {
        attr("role", "tablist")
        style {
            display(DisplayStyle.Flex)
            flexWrap(FlexWrap.Wrap)
            alignItems(AlignItems.Stretch)
            height(TABS_HEIGHT_PX.px)
            property("gap", "0.25rem")
            property("border-bottom", "1px solid ${Theme.border}")
            marginBottom(1.5.cssRem)
        }
    }) {
        labels.forEachIndexed { index, label ->
            val isActive = index == active
            Button(attrs = {
                attr("role", "tab")
                attr("aria-selected", isActive.toString())
                onClick { active = index; DeepLink.showTab(label); onSelect(index) }
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    property("background", "transparent")
                    property("border", "none")
                    property("cursor", "pointer")
                    property("font-family", Theme.fontStack)
                    fontSize(1.cssRem)
                    padding(0.px, 1.2.cssRem)
                    marginBottom((-1).px)
                    color(Color(if (isActive) Theme.text else Theme.textMuted))
                    property("border-bottom", if (isActive) "2px solid ${Theme.accent}" else "2px solid transparent")
                }
            }) { Text(label) }
        }
    }
    Div(attrs = { attr("role", "tabpanel") }) {
        CompositionLocalProvider(LocalTabSlug provides slugify(labels[active])) { content(active) }
    }
}
